/* eslint-disable no-console */
import React, { FC, useCallback, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useAnalytics } from 'reactfire'
import { Link } from 'react-router-dom'

import { iRootState } from '../../../store'
import PhotoPicker from '../../../components/PhotoPicker'
import { LoadingScreen } from '../../../components/Loading'

import {
  Wrap,
  Navigation,
  Content,
  NavigationButton,
  ProfileFrame,
  ProfilePicture,
  EditWrap,
  ProfileName,
  Username,
  EditButton,
  VersionWrap,
  MobileSubheader,
  OffsetMenuGroup,
} from './styled'
import LogoutModal from './components/LogoutModal/LogoutModal'
import DeleteModal from './components/DeleteModal/DeleteModal'

import {
  EditPhotoIcon,
  TermsIcon,
  LogOutIcon,
  PrivacyIcon,
  EnvelopeIcon,
  DeleteIcon,
} from 'src/components/Icons'
import EditProfile from 'src/components/EditProfile'
import { City } from 'src/store/models/user'
import config from 'src/config'
import { updateUser } from 'src/api/user/user'
import { analyticsEvents } from 'src/constants/analytics/events'
import { TERMS, PRIVACY } from 'src/constants/routes'

const stateSelector = (state: iRootState) => ({
  profilePicture: state.user.data?.profilePicture,
  firstName: state.user.data?.firstName,
  birthday: state.user.data?.birthday,
  username: state.user.data?.username,
  id: state.user.data?.id,
})

type MainProps = {
  prevCity?: City
}

const Main: FC<MainProps> = ({ prevCity }) => {
  const { logEvent } = useAnalytics()

  const [isLoading, setIsLoading] = useState(false)
  const [age, setAge] = useState<number>()
  const [openEdit, setOpenEdit] = useState(false)
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const { profilePicture, firstName, birthday, username, id } = useSelector(
    stateSelector
  )

  const onUpdate = useCallback(
    async ({ data, state }) => {
      if (state === 'loading') {
        console.log('Uploading photo')
        setIsLoading(true)
      }
      if (state === 'error') {
        console.log('Error while uploading photo')
        setIsLoading(false)
      }
      if (state === 'success') {
        console.log('Photo uploaded:', data)
        const updatedUser = await updateUser({ profilePicture: data })
        console.log(updatedUser)
        setIsLoading(false)
      }
    },
    [setIsLoading]
  )

  useEffect(() => {
    const now = new Date()
    const birth = new Date(birthday || '')
    // eslint-disable-next-line no-shadow
    let age = now.getFullYear() - birth.getFullYear()
    const m = now.getMonth() - birth.getMonth()

    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) {
      age--
    }

    setAge(age)
  }, [birthday])

  useEffect(() => {
    logEvent(analyticsEvents.userProfileOpened)
  }, [logEvent])

  return (
    <Wrap>
      {isLoading && <LoadingScreen />}
      <EditProfile
        setIsLoading={setIsLoading}
        setOpenEdit={setOpenEdit}
        openEdit={openEdit}
        prevCity={prevCity}
      />
      <LogoutModal
        isOpen={isLogoutModalOpen}
        closeModal={() => setLogoutModalOpen(false)}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        closeModal={() => setDeleteModalOpen(false)}
      />
      <Content>
        <ProfileFrame>
          <PhotoPicker onUpdate={onUpdate} uid={id || ''} />
          <ProfilePicture>
            {profilePicture && <img src={profilePicture} alt="Profile" />}
          </ProfilePicture>
          <EditWrap>
            <PhotoPicker onUpdate={onUpdate} uid={id || ''} />
            <EditPhotoIcon />
          </EditWrap>
        </ProfileFrame>
        <ProfileName>
          {firstName}, {age}
        </ProfileName>
        <Username>@{username}</Username>
        <EditButton onClick={() => setOpenEdit(true)}>
          <p>Edit profile</p>
        </EditButton>
        <VersionWrap>v{config.version}</VersionWrap>
      </Content>
      <Navigation>
        <MobileSubheader>Manage Account</MobileSubheader>
        <NavigationButton onClick={() => setDeleteModalOpen(true)}>
          <DeleteIcon />
          <p>Delete account</p>
        </NavigationButton>
        <Link to={PRIVACY}>
          <NavigationButton>
            <PrivacyIcon />
            <p>Privacy Policy</p>
          </NavigationButton>
        </Link>
        <Link to={TERMS}>
          <NavigationButton>
            <TermsIcon />
            <p>Terms and Services</p>
          </NavigationButton>
        </Link>
        <OffsetMenuGroup>
          <a href="mailto:hello@pumpkinpie.com">
            <NavigationButton>
              <EnvelopeIcon />
              <p>Contact Us</p>
            </NavigationButton>
          </a>
          <NavigationButton last onClick={() => setLogoutModalOpen(true)}>
            <LogOutIcon />
            <p>Log out</p>
          </NavigationButton>
        </OffsetMenuGroup>
      </Navigation>
    </Wrap>
  )
}

export default Main
